import { useNavigate, useLocation, NavLink } from 'react-router-dom';

import SidebarTrigger from '@/components/header/SidebarTrigger';
import { Sidebar, SidebarHeader, SidebarRail } from '@/components/ui/sidebar';

import NewChatButton from '../header/NewChat';
import SearchChats from './Search';
import { ThreadHistory } from './ThreadHistory';
import { useAudio, useAuth, useConfig } from '@chainlit/react-client';


import { cn } from '@/lib/utils';

// Base (non-root) nav items
const navItems = [{ label: 'Home', to: '/' }];

// Extended items shown only on root path "/"
const rootExtraNavItems = [
  { label: 'AskAWatch', to: '/askawatch' },
  { label: 'Change Validation', to: '/changevalidation' },
  { label: 'Incident Communications', to: '/incidentcommunication' },
  { label: 'Metrics', to: '/metrics' },
  { label: 'Feedback', to: '/feedback' }
  // { label: 'LCNC', to: '/lcnc' }
];

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

export default function LeftSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { data } = useAuth();
  const { user, logout } = useAuth();
  const userEmail = (user?.metadata as any)?.email || '';
  const userRole = (user?.metadata as any)?.role || ''; // NEW: capture role
  const { config } = useConfig();
  const historyEnabled = data?.requireLogin && config?.dataPersistence;

  const navigate = useNavigate();
  const location = useLocation();
  const isRoot = location.pathname === '/';
  const isAskAWatch = location.pathname === '/askawatch';

  const filteredRootExtraNavItems = rootExtraNavItems.filter(item =>
    item.label !== 'Metrics' || userRole === 'admin'
  );
  const items = isRoot ? [...navItems, ...filteredRootExtraNavItems] : navItems;

  return (
    <Sidebar {...props} className="border-none pt-14">
      <SidebarHeader className="py-3">
        <div className="flex items-center justify-between">

          {/* <SidebarTrigger /> */}
          <div className="flex items-center">
          {isAskAWatch  && (
            <>
              <NewChatButton navigate={navigate} />
            </>
          )}
          {historyEnabled  && (
            <>
              <SearchChats />
            </>
          )}
          </div>
        </div>
      </SidebarHeader>
      <div className={cn("flex flex-col", isRoot && "justify-start flex-1 pt-[100%]")}>
        {/* Render all items except Feedback and Metrics */}
        {items
          .filter(item => item.label !== 'Feedback' && item.label !== 'Metrics')
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                cn(
                  'group flex items-center rounded-md px-5 py-2 text-sm font-medium transition-colors',
                  'hover:bg-white/5 hover:text-white',
                  isActive ? 'text-white' : 'text-neutral-300',
                )
              }
            >
              {!isRoot && (
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              )}
              <span className="truncate transition-opacity duration-200">
                {item.label}
              </span>
            </NavLink>
          ))}

        {/* Spacer to push Metrics and Feedback to bottom */}
        <div className="flex-1" />

        {/* Render Metrics at the bottom, only for Admin and except on Metrics page */}
        {userRole === 'admin' && location.pathname !== '/metrics' && items.some(item => item.label === 'Metrics') && (
          <NavLink
            key="metrics-bottom"
            to="/metrics"
            end
            className={({ isActive }) =>
              cn(
                'group flex items-center rounded-md px-5 text-sm font-medium transition-colors mb-2',
                'hover:bg-white/5 hover:text-white',
                isActive ? 'text-white' : 'text-neutral-300',
              )
            }
          >
            <span className="truncate transition-opacity duration-200">
              Metrics
            </span>
          </NavLink>
        )}

        {/* Render Feedback at the bottom, except on Feedback page */}
        {location.pathname !== '/feedback' && items.some(item => item.label === 'Feedback') && (
          <NavLink
            key="feedback-bottom"
            to="/feedback"
            end
            className={({ isActive }) =>
              cn(
                'group flex items-center rounded-md px-5 py-2 text-sm font-medium transition-colors mb-2',
                'hover:bg-white/5 hover:text-white',
                isActive ? 'text-white' : 'text-neutral-300',
              )
            }
          >
            <span className="truncate transition-opacity duration-200">
              Feedback
            </span>
          </NavLink>
        )}
      </div>
      <SidebarRail />
    </Sidebar>
  );
}
